import { apiClient } from "@/lib/api";
import type { ActivityLogDiff } from "@/types/admin";
import type { PaginatedResponse } from "@/types/routes";

export interface AuditLogEntry {
  id: number;
  /** User email or null for system actions */
  user: string | null;
  action: "create" | "update" | "delete";
  model_name: string;
  object_id: number;
  object_repr: string;
  /**
   * For updates: `{ fieldName: [oldValue, newValue] }`; for create/delete: `{}`.
   *
   * Typed as {@link ActivityLogDiff} — the SAME type the admin Activity Log uses —
   * because both surfaces render rows from the one `AuditLog` table and must not
   * disagree about their shape. The previous `Record<string, [unknown, unknown]>`
   * was wrong twice over: values are scalars (never nested objects), and the
   * change is not always a pair. Measured over the last 400 rows on dev: 1279
   * strings + 133 nulls inside pairs, and 108 rows whose change is a bare
   * dict/str/int rather than a two-element list. `ActivityLogChange` already
   * covers the object form, and `normalizeDiffChange` collapses both to a pair
   * at render time — so this widens the type to the truth instead of asserting a
   * shape the data does not always have.
   */
  diff: ActivityLogDiff;
  created_at: string;
}

export interface GetAuditLogParams {
  model_name: string;
  object_id: number;
  page?: number;
}

/**
 * Fetches a paginated audit log for a given model instance.
 * GET /api/v1/audit-log/?model_name=<model>&object_id=<id>&page=<n>
 *
 * Only accessible to company_admin and superadmin roles.
 */
export async function getAuditLog(
  params: GetAuditLogParams,
): Promise<PaginatedResponse<AuditLogEntry>> {
  const response = await apiClient.get<PaginatedResponse<AuditLogEntry>>(
    "/v1/audit-log/",
    { params },
  );
  return response.data;
}
